import { useEffect, useMemo, useState } from "react";
import api, { money } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  Banknote, Plus, Sparkles, CheckCircle2, Clock, Trash2, ArrowRight, FileDown,
} from "lucide-react";
import { toast } from "sonner";

/* ──────────────────────────────────────────────────────────────────────────
   BuyerHubFinancing — Off-platform invoice financing for ANY buyer.
   Even if the supplier isn't on verqo, the buyer can upload an invoice
   and accept a synthetic financing offer (we pay the supplier in 24h,
   buyer repays at due date). Day-one revenue + day-one buyer value.
   ────────────────────────────────────────────────────────────────────── */
const STATUS_META = {
  uploaded:           { label: "Tracked",            chip: "bg-secondary text-foreground/60 border border-border" },
  financing_offered:  { label: "Offer ready",        chip: "bg-violet-50 text-violet-700 border border-violet-200" },
  financed:           { label: "Financed",           chip: "bg-emerald-50 text-emerald-700 border border-emerald-200" },
  repaid:             { label: "Repaid",             chip: "bg-blue-50 text-blue-700 border border-blue-200" },
};

export default function BuyerHubFinancing() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    supplier_name: "", supplier_email: "", invoice_number: "",
    amount: "", issued_date: "",
    due_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
    note: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [accepting, setAccepting] = useState(null);

  const load = () => {
    setLoading(true);
    return api.get("/buyer/hub/external-invoices")
      .then(({ data }) => setInvoices(data))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const totals = useMemo(() => {
    const offered = invoices.filter(i => i.status === "financing_offered");
    const financed = invoices.filter(i => i.status === "financed");
    return {
      total:           invoices.length,
      total_amount:    invoices.reduce((s, i) => s + (i.amount || 0), 0),
      offered_amount:  offered.reduce((s, i) => s + (i.amount || 0), 0),
      financed_count:  financed.length,
      financed_amount: financed.reduce((s, i) => s + (i.amount || 0), 0),
    };
  }, [invoices]);

  const submitInvoice = async () => {
    if (!form.supplier_name.trim()) return toast.error("Supplier name required");
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Amount must be > 0");
    if (!form.due_date) return toast.error("Due date required");
    setSubmitting(true);
    try {
      const { data } = await api.post("/buyer/hub/external-invoices", {
        supplier_name:  form.supplier_name.trim(),
        supplier_email: form.supplier_email.trim() || undefined,
        invoice_number: form.invoice_number.trim() || undefined,
        amount:         Number(form.amount),
        issued_date:    form.issued_date ? new Date(form.issued_date).toISOString() : undefined,
        due_date:       new Date(form.due_date).toISOString(),
        note:           form.note.trim() || undefined,
      });
      toast.success(`Offer ready · ${(data.financing_offer.rate * 100).toFixed(2)}% fee`);
      setAdding(false);
      setForm({
        supplier_name: "", supplier_email: "", invoice_number: "",
        amount: "", issued_date: "",
        due_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
        note: "",
      });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setSubmitting(false);
    }
  };

  const acceptOffer = async (inv) => {
    setAccepting(inv.id);
    try {
      await api.post(`/buyer/hub/external-invoices/${inv.id}/finance`);
      toast.success(`Financed · supplier paid within 24h`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not accept");
    } finally {
      setAccepting(null);
    }
  };

  const deleteInvoice = async (id) => {
    if (!confirm("Delete this invoice?")) return;
    await api.delete(`/buyer/hub/external-invoices/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="buyer-hub-financing">
      <div className="border-b border-border bg-background/80 backdrop-blur sticky top-0 z-20 px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
        <div>
          <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50 inline-flex items-center gap-1.5">
            <Banknote className="w-3 h-3"/>verqo Hub · Off-platform invoice financing
          </div>
          <h1 className="display text-2xl mt-1 tracking-tight">Finance any invoice. From any supplier.</h1>
        </div>
        <Button onClick={() => setAdding(true)} data-testid="add-external-invoice-btn"
          className="rounded-full h-10 px-4 bg-foreground text-background hover:bg-foreground/90">
          <Plus className="w-4 h-4 mr-1.5"/>Upload invoice
        </Button>
      </div>

      <div className="p-8 space-y-6">
        {/* Pitch banner */}
        <div className="rounded-2xl bg-foreground text-background p-6 grid md:grid-cols-3 gap-4" data-testid="financing-pitch">
          <div>
            <div className="text-[10px] mono uppercase tracking-[0.2em] opacity-60">How it works</div>
            <div className="display text-xl mt-1.5">1 · Upload invoice</div>
            <div className="text-xs opacity-70 mt-1">From any supplier — even ones not on verqo.</div>
          </div>
          <div>
            <div className="text-[10px] mono uppercase tracking-[0.2em] opacity-60">Step 2</div>
            <div className="display text-xl mt-1.5">2 · We pay the supplier in 24h</div>
            <div className="text-xs opacity-70 mt-1">No more chasing for early-payment discounts.</div>
          </div>
          <div>
            <div className="text-[10px] mono uppercase tracking-[0.2em] opacity-60">Step 3</div>
            <div className="display text-xl mt-1.5">3 · You repay at the due date</div>
            <div className="text-xs opacity-70 mt-1">Net-30/60/90 — cash-flow stays smooth.</div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="financing-kpis">
          <Kpi label="Invoices uploaded"    value={totals.total}/>
          <Kpi label="Total tracked"        value={money(totals.total_amount)}/>
          <Kpi label="Available to finance" value={money(totals.offered_amount)} accent="violet"/>
          <Kpi label="Financed to date"     value={money(totals.financed_amount)} accent="emerald" big/>
        </div>

        <div className="space-y-3">
          {loading && <div className="rounded-2xl bg-white border border-border p-12 text-center text-sm text-foreground/55">Loading…</div>}

          {!loading && invoices.length === 0 && (
            <div className="rounded-2xl bg-white border border-border p-12 text-center text-sm text-foreground/55" data-testid="financing-empty">
              <Banknote className="w-8 h-8 mx-auto mb-3 text-foreground/30"/>
              <div className="display text-lg">Upload your first invoice</div>
              <div className="text-xs mt-1 max-w-md mx-auto">Add any unpaid invoice from any supplier. We'll instantly compute a financing offer — supplier paid in 24h, you repay at the original due date.</div>
              <Button onClick={() => setAdding(true)} className="mt-4 rounded-full bg-foreground text-background hover:bg-foreground/90">
                <Plus className="w-4 h-4 mr-1.5"/>Upload invoice
              </Button>
            </div>
          )}

          {!loading && invoices.map(inv => {
            const meta = STATUS_META[inv.status] || STATUS_META.uploaded;
            const offer = inv.financing_offer || {};
            const showAccept = inv.status === "financing_offered";
            return (
              <div key={inv.id} className="rounded-2xl bg-white border border-border p-5" data-testid={`invoice-row-${inv.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="display text-lg">{inv.supplier_name}</span>
                      <span className={`chip text-[11px] ${meta.chip}`}>{meta.label}</span>
                      {inv.invoice_number && <span className="mono text-xs text-foreground/55">· {inv.invoice_number}</span>}
                    </div>
                    {inv.supplier_email && <div className="text-xs text-foreground/55 mt-0.5">{inv.supplier_email}</div>}
                    {inv.file?.name && (
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/buyer/hub/external-invoices/${inv.id}/file`}
                        target="_blank" rel="noopener noreferrer"
                        data-testid={`invoice-file-${inv.id}`}
                        className="inline-flex items-center gap-1.5 text-xs text-foreground/65 hover:text-foreground mt-1.5 underline-offset-2 hover:underline"
                      >
                        <FileDown className="w-3.5 h-3.5"/>{inv.file.name} <span className="text-foreground/40">({(inv.file.size / 1024).toFixed(1)} KB)</span>
                      </a>
                    )}
                    <div className="mt-3 grid grid-cols-3 gap-4 text-sm max-w-md">
                      <div>
                        <div className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50">Invoice</div>
                        <div className="display mt-0.5">{money(inv.amount)}</div>
                      </div>
                      <div>
                        <div className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50">Due date</div>
                        <div className="mt-0.5">{new Date(inv.due_date).toLocaleDateString()}</div>
                      </div>
                      <div>
                        <div className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50">Days to maturity</div>
                        <div className="mt-0.5">{offer.days_to_maturity ?? "—"}</div>
                      </div>
                    </div>
                  </div>
                  <button onClick={() => deleteInvoice(inv.id)} className="text-foreground/40 hover:text-rose-600 p-1" data-testid={`invoice-delete-${inv.id}`}>
                    <Trash2 className="w-4 h-4"/>
                  </button>
                </div>

                {/* Offer / financed summary */}
                {showAccept && (
                  <div className="mt-4 rounded-xl bg-violet-50 border border-violet-200 p-4 grid md:grid-cols-4 gap-3 items-center" data-testid={`invoice-offer-${inv.id}`}>
                    <Stat label="Fee"                value={`${(offer.rate * 100).toFixed(2)}%`}/>
                    <Stat label="Supplier gets"     value={money(offer.payout_to_supplier)}/>
                    <Stat label="You repay"         value={money(offer.buyer_repays)}/>
                    <Button onClick={() => acceptOffer(inv)} disabled={accepting === inv.id}
                      data-testid={`accept-offer-${inv.id}`}
                      className="rounded-full bg-foreground text-background hover:bg-foreground/90">
                      {accepting === inv.id ? "Processing…" : <>Accept offer <ArrowRight className="w-4 h-4 ml-1.5"/></>}
                    </Button>
                  </div>
                )}
                {inv.status === "financed" && (
                  <div className="mt-4 rounded-xl bg-emerald-50 border border-emerald-200 p-4 flex items-center gap-3 text-sm" data-testid={`invoice-financed-${inv.id}`}>
                    <CheckCircle2 className="w-5 h-5 text-emerald-600"/>
                    <div className="flex-1">
                      <div className="font-medium">Supplier paid · {money(offer.payout_to_supplier)}</div>
                      <div className="text-xs text-foreground/60">You repay {money(offer.buyer_repays)} on {new Date(inv.due_date).toLocaleDateString()}.</div>
                    </div>
                    {inv.financing_accepted_at && (
                      <div className="text-xs text-foreground/55 inline-flex items-center gap-1">
                        <Clock className="w-3 h-3"/>{new Date(inv.financing_accepted_at).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="rounded-2xl bg-secondary/30 border border-border p-4 flex items-start gap-3 text-xs text-foreground/65">
          <Sparkles className="w-4 h-4 text-foreground/50 shrink-0 mt-0.5"/>
          <div>
            <span className="font-medium text-foreground">No supplier change required. </span>
            We pay the supplier directly via ACH or wire — they don't have to sign up for anything. Pricing scales with your verqo Trade Score (better score → lower fee). Once accepted, the financing line shows up on your <a href="/buyer/payments#credit" className="underline hover:no-underline">Credit & Terms</a> ledger.
          </div>
        </div>
      </div>

      {/* Upload-invoice dialog */}
      <Dialog open={adding} onOpenChange={(v) => !v && setAdding(false)}>
        <DialogContent className="max-w-lg" data-testid="add-invoice-dialog">
          <DialogHeader>
            <DialogTitle className="display text-2xl tracking-tight">Upload an invoice for financing</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-foreground/55 -mt-1">Works with any supplier — even ones not on verqo. We'll instantly compute a financing offer.</p>

          <div className="mt-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Supplier name *">
                <Input value={form.supplier_name} onChange={e => setForm({...form, supplier_name: e.target.value})}
                  placeholder="Acme Cosmetics Co." data-testid="inv-supplier-name"/>
              </Field>
              <Field label="Supplier email">
                <Input type="email" value={form.supplier_email}
                  onChange={e => setForm({...form, supplier_email: e.target.value})}
                  placeholder="ar@acme.example" data-testid="inv-supplier-email"/>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Invoice #">
                <Input value={form.invoice_number} onChange={e => setForm({...form, invoice_number: e.target.value})}
                  placeholder="ACME-INV-2384" data-testid="inv-number"/>
              </Field>
              <Field label="Amount *">
                <Input type="number" min="0" step="0.01" value={form.amount}
                  onChange={e => setForm({...form, amount: e.target.value})}
                  placeholder="12500.00" data-testid="inv-amount"/>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Issued date">
                <Input type="date" value={form.issued_date} onChange={e => setForm({...form, issued_date: e.target.value})}
                  data-testid="inv-issued"/>
              </Field>
              <Field label="Due date *">
                <Input type="date" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})}
                  min={new Date().toISOString().slice(0, 10)} data-testid="inv-due"/>
              </Field>
            </div>
            <Field label="Note">
              <Textarea rows={2} value={form.note} onChange={e => setForm({...form, note: e.target.value})}
                placeholder="Internal note." data-testid="inv-note"/>
            </Field>
          </div>

          <div className="mt-4 flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setAdding(false)} className="rounded-full">Cancel</Button>
            <Button onClick={submitInvoice} disabled={submitting} data-testid="inv-submit"
              className="rounded-full bg-foreground text-background hover:bg-foreground/90">
              {submitting ? "Computing offer…" : "Get financing offer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ label, value, big, accent }) {
  const ringMap = { violet: "ring-violet-200", emerald: "ring-emerald-200" };
  const colorMap = { violet: "text-violet-700", emerald: "text-emerald-700" };
  return (
    <div className={`rounded-2xl border border-border bg-white p-5 ${accent ? `ring-1 ${ringMap[accent]}` : ""}`}>
      <div className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50">{label}</div>
      <div className={`display ${big ? "text-3xl" : "text-2xl"} mt-2 tracking-tight ${accent ? colorMap[accent] : ""}`}>{value}</div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50">{label}</div>
      <div className="display mt-0.5">{value}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50 mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}
